import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { responderForCampus, seedAlerts, seedIncidents } from '../data/safety';
import type { AlertDisclosure, AlertKind, AlertState, EmergencyAlert, SafetyIncident } from '../types/safety';
interface RaiseInput {
  kind: AlertKind;
  campusId: string;
  disclosure: AlertDisclosure;
  coordinates: EmergencyAlert['coordinates'];
  placeHint: string;
  note: string;
  raisedBy: EmergencyAlert['raisedBy'];
  trustedContacts: string[];
}
interface SafetyContextValue {
  alerts: EmergencyAlert[];
  incidents: SafetyIncident[];
  /** Returns the created alert so the caller can follow its state. */
  raise: (input: RaiseInput) => EmergencyAlert;
  cancel: (id: string, actor: string) => void;
  /** Responder side. */
  acknowledge: (id: string, responder: string) => void;
  markResponding: (id: string, responder: string, note: string) => void;
  close: (id: string, responder: string, outcome: string) => void;
  updateIncident: (id: string, status: SafetyIncident['status'], note: string, actor: string) => void;
  fileIncident: (input: Omit<SafetyIncident, 'id' | 'reference' | 'status' | 'responderNote'>) => void;
}
const SafetyContext = createContext<SafetyContextValue | null>(null);
function now(): string {
  return new Date().toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit'
  });
}
export function SafetyProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [alerts, setAlerts] = useState<EmergencyAlert[]>(seedAlerts);
  const [incidents, setIncidents] = useState<SafetyIncident[]>(seedIncidents);
  const patch = useCallback((id: string, next: Partial<EmergencyAlert>, event?: {
    label: string;
    actor: string;
  }) => {
    setAlerts(current => current.map(alert => alert.id === id ? {
      ...alert,
      ...next,
      timeline: event ? [...alert.timeline, {
        at: now(),
        label: event.label,
        actor: event.actor
      }] : alert.timeline
    } : alert));
  }, []);
  const raise = useCallback<SafetyContextValue['raise']>(input => {
    const responder = responderForCampus(input.campusId);
    const id = `alert-${Date.now()}`;
    const reference = `SOS-${4500 + Math.floor(Math.random() * 400)}`;

    // The state is decided by whether a channel actually exists — never
    // optimistically reported as delivered.
    const state: AlertState = !responder || responder.channel === 'none' ? 'undeliverable' : responder.channel === 'console' ? 'delivered' : 'recorded';
    const notified = input.disclosure.trustedContacts ? input.trustedContacts : [];
    const alert: EmergencyAlert = {
      id,
      reference,
      kind: input.kind,
      state,
      campusId: input.campusId,
      raisedBy: input.raisedBy,
      raisedAt: new Date().toLocaleString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }),
      disclosure: input.disclosure,
      coordinates: input.disclosure.location ? input.coordinates : null,
      placeHint: input.placeHint,
      note: input.disclosure.note ? input.note : '',
      trustedContactsNotified: notified,
      responder: null,
      outcome: null,
      timeline: [{
        at: now(),
        label: 'Alert raised by student',
        actor: `@${input.raisedBy.handle}`
      }, ...(state === 'undeliverable' ? [{
        at: now(),
        label: 'No response channel on this campus — alert recorded, not delivered',
        actor: 'Campuzon'
      }] : state === 'delivered' ? [{
        at: now(),
        label: `Delivered to ${responder!.unit} desk`,
        actor: 'Campuzon'
      }] : [{
        at: now(),
        label: `Recorded — relayed to ${responder!.unit} by phone`,
        actor: 'Campuzon'
      }]), ...(notified.length > 0 ? [{
        at: now(),
        label: `Trusted contacts notified: ${notified.join(', ')}`,
        actor: 'Campuzon'
      }] : [])]
    };
    setAlerts(current => [alert, ...current]);
    return alert;
  }, []);
  const value = useMemo<SafetyContextValue>(() => ({
    alerts,
    incidents,
    raise,
    cancel: (id, actor) => patch(id, {
      state: 'cancelled'
    }, {
      label: 'Withdrawn by the student',
      actor
    }),
    acknowledge: (id, responder) => patch(id, {
      state: 'acknowledged',
      responder
    }, {
      label: 'Acknowledged',
      actor: responder
    }),
    markResponding: (id, responder, note) => patch(id, {
      state: 'responding',
      responder
    }, {
      label: note || 'Responding',
      actor: responder
    }),
    close: (id, responder, outcome) => patch(id, {
      state: 'closed',
      responder,
      outcome
    }, {
      label: 'Closed with outcome',
      actor: responder
    }),
    updateIncident: (id, status, note, actor) => setIncidents(current => current.map(incident => incident.id === id ? {
      ...incident,
      status,
      responderNote: `${note} — ${actor}`
    } : incident)),
    fileIncident: input => setIncidents(current => [{
      ...input,
      id: `inc-${Date.now()}`,
      reference: `INC-${2300 + Math.floor(Math.random() * 200)}`,
      status: 'Received',
      responderNote: null
    }, ...current])
  }), [alerts, incidents, raise, patch]);
  return <SafetyContext.Provider value={value} data-id="element-6064" data-mp="1wczrz6mo7q80">{children}</SafetyContext.Provider>;
}
export function useSafety(): SafetyContextValue {
  const context = useContext(SafetyContext);
  if (!context) throw new Error('useSafety must be used inside SafetyProvider');
  return context;
}